import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Calendar, 
  Users, 
  Stethoscope, 
  Shield, 
  Phone, 
  Mail, 
  MapPin,
  ArrowRight,
  Building2,
  UserPlus,
  MessageCircle,
  Facebook,
  Instagram
} from 'lucide-react';
import siteConfig from '../config/siteConfig';

export default function PublicHomePage() {
  const { company, contact, logo, homepage, social } = siteConfig;

  const serviceIcons = [Stethoscope, Calendar, Shield, Users];

  // WhatsApp link generator
  const getWhatsAppLink = () => {
    if (!social?.whatsapp?.enabled) return null;
    const message = encodeURIComponent(social.whatsapp.message || '');
    return `https://wa.me/${social.whatsapp.number}${message ? `?text=${message}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="public-home-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo.url} alt={logo.alt} className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <h1 className="font-bold text-slate-900">{company.name}</h1>
              <p className="text-xs text-slate-500">{company.tagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">Staff Login</Button>
            </Link>
            <Link to="/patient-request">
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800">
                {homepage.patientCard.buttonText}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
            {homepage.headline} <span className="text-blue-600">{homepage.headlineHighlight}</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10">
            {homepage.subtitle}
          </p>
          
          {/* CTA Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Patient Request Card */}
            <Card className="border-0 shadow-xl bg-white overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-white">
                  <UserPlus className="w-12 h-12 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">{homepage.patientCard.title}</h2>
                  <p className="opacity-90">{homepage.patientCard.subtitle}</p>
                </div>
                <div className="p-6">
                  <ul className="text-left space-y-3 mb-6">
                    {homepage.patientCard.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <Stethoscope className="w-4 h-4 text-blue-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/patient-request">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 group-hover:scale-[1.02] transition-transform">
                      {homepage.patientCard.buttonText}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Event Quote Card */}
            <Card className="border-0 shadow-xl bg-white overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-white">
                  <Building2 className="w-12 h-12 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">{homepage.eventCard.title}</h2>
                  <p className="opacity-90">{homepage.eventCard.subtitle}</p>
                </div>
                <div className="p-6">
                  <ul className="text-left space-y-3 mb-6">
                    {homepage.eventCard.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <Shield className="w-4 h-4 text-slate-700" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/event-quote">
                    <Button className="w-full bg-slate-800 hover:bg-slate-900 group-hover:scale-[1.02] transition-transform">
                      {homepage.eventCard.buttonText}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-slate-900">Our Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {homepage.services.map((service, i) => {
              const Icon = serviceIcons[i] || Stethoscope;
              return (
                <Card key={i} className="text-center p-6 border-0 shadow-md hover:shadow-lg transition-shadow">
                  <Icon className="w-10 h-10 mx-auto mb-4 text-blue-600" />
                  <h3 className="font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-500">{service.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Contact Us</h2>
              <p className="text-slate-300 mb-8">
                Have questions? Our team is here to help. Reach out to us and we'll respond as soon as possible.
              </p>
              <div className="space-y-4">
                {/* Phone Numbers - Multiple clickable links */}
                <div className="flex items-start gap-3 p-2 -ml-2">
                  <div className="p-2 rounded-lg bg-slate-800 mt-0.5">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Phone</p>
                    {(contact.phones || [contact.phone]).map((num, i) => (
                      <a key={i} href={`tel:${num.replace(/[^+\d]/g, '')}`} className="block font-medium hover:text-emerald-400 transition-colors">
                        {num}
                      </a>
                    ))}
                  </div>
                </div>

                {/* WhatsApp - Clickable */}
                {social?.whatsapp?.enabled && (
                  <a 
                    href={getWhatsAppLink()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:bg-slate-800 p-2 rounded-lg transition-colors -ml-2"
                  >
                    <div className="p-2 rounded-lg bg-green-600">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">WhatsApp</p>
                      <p className="font-medium">Message us on WhatsApp</p>
                    </div>
                  </a>
                )}

                {/* Email - Clickable */}
                <a href={`mailto:${contact.email}`} className="flex items-center gap-3 hover:bg-slate-800 p-2 rounded-lg transition-colors -ml-2">
                  <div className="p-2 rounded-lg bg-slate-800">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Email</p>
                    <p className="font-medium">{contact.email}</p>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-center gap-3 p-2 -ml-2">
                  <div className="p-2 rounded-lg bg-slate-800">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Location</p>
                    <p className="font-medium">{contact.fullAddress || contact.address}</p>
                  </div>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <p className="text-sm text-slate-400 mb-4">Follow us on social media</p>
                <div className="flex gap-4">
                  {social?.facebook?.enabled && (
                    <a 
                      href={social.facebook.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-slate-800 hover:bg-blue-600 transition-colors"
                      title="Follow us on Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {social?.instagram?.enabled && (
                    <a 
                      href={social.instagram.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-slate-800 hover:bg-pink-600 transition-colors"
                      title="Follow us on Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {social?.whatsapp?.enabled && (
                    <a 
                      href={getWhatsAppLink()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-slate-800 hover:bg-green-600 transition-colors"
                      title="Message us on WhatsApp"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Action Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Link to="/patient-request" className="block">
                <Card className="p-6 bg-slate-800 border-slate-700 hover:bg-slate-700 transition-colors text-center">
                  <UserPlus className="w-8 h-8 mx-auto mb-3 text-blue-400" />
                  <p className="font-medium">{homepage.patientCard.buttonText}</p>
                </Card>
              </Link>
              <Link to="/event-quote" className="block">
                <Card className="p-6 bg-slate-800 border-slate-700 hover:bg-slate-700 transition-colors text-center">
                  <Building2 className="w-8 h-8 mx-auto mb-3 text-blue-400" />
                  <p className="font-medium">{homepage.eventCard.buttonText}</p>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-950 text-slate-400 text-center text-sm">
        <div className="max-w-6xl mx-auto">
          <p>© {company.yearFounded} {company.name}. All rights reserved.</p>
          <p className="mt-2">{homepage.footerText}</p>
        </div>
      </footer>
    </div>
  );
}
